import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonPopover, IonicModule } from '@ionic/angular';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { UserNotificationService } from 'src/app/services/user-notification.service';
import { AppUserRegisterRequest, AppUserRegisterResponse, UserNotificationViewModel } from 'src/app/models/viewmodels';
import { AuthService } from 'src/app/services/auth.service';
import { UserInfoService } from 'src/app/services/user-info.service';
import { DataService } from 'src/app/services/data-api.service';
import { environment } from 'src/environments/environment';
import { UserNotificationComponent } from '../../shared/user-notification/user-notification.component';
import { catchError, map, Observable, of } from 'rxjs';
import { ValidatorsHelper } from '../../util/validators-helper';
import { ErrorMessagesComponent } from 'src/app/components/util/error-messages/error-messages.component';
import { TogglePasswordDirective } from 'src/app/directives/toggle-password.directive';
import { FileService } from 'src/app/services/file.service';
import { NotificationService } from 'src/app/services/notification.service';
import { NotificationTypeEnum } from 'src/app/models/config-view-models';
import { documentOutline, peopleOutline, barChartOutline, arrowForwardOutline } from 'ionicons/icons';
import { addIcons } from 'ionicons';

@Component({
  standalone: true,
  selector: 'user-register',
  templateUrl: './user-register.component.html',
  styleUrls: ['./user-register.component.scss'],
  imports: [
    IonicModule,
    FormsModule,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    UserNotificationComponent,
    ErrorMessagesComponent,
    TogglePasswordDirective,
  ],
})
export class UserRegisterComponent implements OnInit {
  //cns: put to interface
  appUserRegisterRequest: AppUserRegisterRequest = {
    FirstName: '',
    LastName: '',
    EmailAddress: '',
    Password: '',
    ConfirmPassword: '',
    DefaultCompanyReference: '',
    IsInternalEmployee: null,
    AgreedToCustomerTermsOfUse: false,
    IsSpecifyManagerPro: false,
  };

  registerUserFormGroup: FormGroup = new FormGroup({});

  logoImagePath: string = '';

  userMessage: string = '';

  validateEmailExists = ValidatorsHelper.validateEmailExists;
  isRequiredField = ValidatorsHelper.isRequired;
  isNotEqualTo = ValidatorsHelper.isNotEqualTo;
  passwordMatchValidator = ValidatorsHelper.passwordMatchValidator;

  constructor(
    private dataService: DataService,
    private fileService: FileService,
    private userInfoService: UserInfoService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private userNotificationService: NotificationService,
  ) {
    this.setRegisterUserFormGroup();

    addIcons({
      documentOutline,
      peopleOutline,
      barChartOutline,
      arrowForwardOutline,
    });
  }

  async ngOnInit() {
    //this.logoImagePath = "/assets/images/QuotifyLogoImage.png"
    try {
      let isEngineerUrl =
        window.location.href.toLowerCase().includes('specifymanagerpro.com') ||
        window.location.href.toLowerCase().includes('localhost:8123');
      if (isEngineerUrl) {
        this.appUserRegisterRequest.IsSpecifyManagerPro = true;
        this.logoImagePath = '/assets/images/SpecifyManagerLogo.png';
      } else {
        this.appUserRegisterRequest.IsSpecifyManagerPro = false;
        this.logoImagePath = '/assets/images/QuotifyLogoImage.png';
      }
    } catch (error) {
      console.error(
        'user-login',
        'ngOnInit',
        'error getting logo image path',
        error,
      );
    }
  }

  async setRegisterUserFormGroup() {
    this.registerUserFormGroup = this.formBuilder.group(
      {
        FirstName: [
          this.appUserRegisterRequest.FirstName,
          [Validators.required],
        ],
        LastName: [this.appUserRegisterRequest.LastName, [Validators.required]],
        EmailAddress: [
          this.appUserRegisterRequest.EmailAddress,
          [Validators.required, Validators.email],
          [
            this.validateEmailExists(
              this.appUserRegisterRequest,
              this.dataService,
            ),
          ],
        ],
        Password: [
          this.appUserRegisterRequest.Password,
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[^A-Za-z\\d])[A-Za-z\\d\\S]{8,}$',
            ),
            this.isNotEqualTo('Password1!'),
          ],
        ],
        ConfirmPassword: [
          this.appUserRegisterRequest.Password,
          [Validators.required],
        ],
        DefaultCompanyReference: [
          this.appUserRegisterRequest.DefaultCompanyReference,
          [Validators.required],
        ],
        AgreedToCustomerTermsOfUse: [false, [Validators.requiredTrue]],
      },

      {
        validator: this.passwordMatchValidator('Password', 'ConfirmPassword'),
      },
    );
  }

  onClickTermsOfUse() {
    this.fileService.handleFileStreamResponse({
      url: environment.apiUrl + 'api/file/GetCustomerTermsOfUsePdf',
      download: true,
      openInNewTab: true,
      fileName: 'CustomerTermsOfUse',
    });
  }
  async registerUser() {
    if (!this.registerUserFormGroup.valid) {
      this.registerUserFormGroup.markAllAsTouched();
      return;
    }

    let registerData = this.registerUserFormGroup.value;
    registerData.IsSpecifyManagerPro = this.logoImagePath.includes(
      'SpecifyManagerLogo.png',
    );
    let registerObs = this.dataService.registerUser(registerData);
    registerObs.subscribe({
      next: (data: AppUserRegisterResponse) => {
        console.log('registerUser response', data);

        if (data?.AppUser?.UserId > 0 && data.Successful) {
          var message = data.ApprovalEmailSent
            ? 'Account created successfully. Thank you for your submission. Our internal team will review your request and email you once your account is ready'
            : 'Account created successfully. However, approval email not sent.Please reach out to administrator(s)';
          let notiticationType = data.ApprovalEmailSent
            ? NotificationTypeEnum.Success
            : NotificationTypeEnum.Warning;
          this.userNotificationService.PresentTypedToast(
            message,
            notiticationType,
            10000,
            () => {
              this.goToLogin();
            },
          );
        } else {
          console.log('registration failed', data);
          this.userNotificationService.PresentTypedToast(
            `Error creating user.${data.UserMessage}`,
            NotificationTypeEnum.Error,
            7000,
            () => {
              this.goToLogin();
            },
          );
        }
      },
      error: (error) => {
        console.log('registerUser error', error);
        this.userNotificationService.PresentTypedToast(
          `API Error creating user. Please check console${error}`,
          NotificationTypeEnum.Error,
          7000,
          () => {
            window.location.href = '/user/user-login';
          },
        );
        console.error('API Error creating user', error);
      },
    });
  }

  public goToLogin() {
    window.location.href = '/user/user-login';
  }
}
